'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/toast';
import {
    Store,
    Mail,
    Lock,
    Key,
    Loader2,
    ArrowRight,
    ShieldCheck,
    BarChart3,
    CreditCard,
    Bell
} from 'lucide-react';

export default function VendorLoginPage() {
    const router = useRouter();
    const toast = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [loginMethod, setLoginMethod] = useState<'email' | 'api_key'>('email');

    // Email login state
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    // API key login state
    const [apiKey, setApiKey] = useState('');

    const handleEmailLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const supabase = createClient();

            // First check if this email is associated with a vendor
            const { data: vendor, error: vendorError } = await supabase
                .from('vendors')
                .select('id, business_name, status')
                .eq('contact_email', email)
                .single();

            if (vendorError || !vendor) {
                throw new Error('No vendor account found with this email. Please register first.');
            }

            if (vendor.status === 'pending') {
                throw new Error('Your vendor application is still under review. We\'ll notify you once approved.');
            }

            if (vendor.status === 'suspended') {
                throw new Error('Your vendor account has been suspended. Please contact support.');
            }

            // Store vendor info for the dashboard
            localStorage.setItem('vendor_id', vendor.id);
            localStorage.setItem('vendor_name', vendor.business_name);

            toast.success(`Welcome back, ${vendor.business_name}!`);
            router.push('/vendor/dashboard');

        } catch (err: any) {
            console.error('Login error:', err);
            toast.error(err.message || 'Login failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleApiKeyLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const supabase = createClient();

            // Verify API key
            const { data: vendor, error: vendorError } = await supabase
                .from('vendors')
                .select('id, business_name, status')
                .eq('api_key', apiKey)
                .single();

            if (vendorError || !vendor) {
                throw new Error('Invalid API key. Please check and try again.');
            }

            if (vendor.status !== 'active') {
                throw new Error(`Your vendor account is ${vendor.status}. Please contact support.`);
            }

            // Store vendor info for the dashboard
            localStorage.setItem('vendor_id', vendor.id);
            localStorage.setItem('vendor_name', vendor.business_name);
            localStorage.setItem('vendor_api_key', apiKey);

            toast.success(`Welcome back, ${vendor.business_name}!`);
            router.push('/vendor/dashboard');

        } catch (err: any) {
            console.error('API key login error:', err);
            toast.error(err.message || 'Login failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 flex items-center justify-center p-4">
            {/* Background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 -left-32 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
            </div>

            <div className="relative w-full max-w-5xl grid lg:grid-cols-2 gap-8 items-center">
                {/* Left side - Branding */}
                <div className="hidden lg:block space-y-8">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                                <Store className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-white">LinkBox</h1>
                                <p className="text-emerald-400 text-sm">Vendor Portal</p>
                            </div>
                        </div>
                        <h2 className="text-3xl font-bold text-white mb-4">
                            Manage Your Agentic Commerce
                        </h2>
                        <p className="text-slate-400">
                            Access your dashboard to view orders, track revenue, and manage
                            your integration with LinkBox AI.
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                                <BarChart3 className="h-5 w-5 text-emerald-400" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-white">Real-time Analytics</h3>
                                <p className="text-sm text-slate-400">Track orders, revenue, and customer engagement</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                                <CreditCard className="h-5 w-5 text-blue-400" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-white">Secure Payments</h3>
                                <p className="text-sm text-slate-400">Stripe-powered transactions with fraud protection</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                                <Bell className="h-5 w-5 text-purple-400" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-white">Instant Notifications</h3>
                                <p className="text-sm text-slate-400">Get notified when orders come in via webhook</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right side - Login form */}
                <Card className="w-full bg-slate-900/80 border-slate-800 backdrop-blur-xl shadow-2xl">
                    <CardHeader className="text-center pb-4">
                        <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
                            <Store className="h-8 w-8 text-emerald-400" />
                            <span className="text-xl font-bold text-white">LinkBox Vendor</span>
                        </div>
                        <CardTitle className="text-2xl text-white">Sign In</CardTitle>
                        <CardDescription className="text-slate-400">
                            Access your vendor dashboard
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Tabs value={loginMethod} onValueChange={(v) => setLoginMethod(v as any)} className="w-full">
                            <TabsList className="grid w-full grid-cols-2 bg-slate-800/50">
                                <TabsTrigger value="email" className="data-[state=active]:bg-emerald-600">
                                    <Mail className="h-4 w-4 mr-2" />
                                    Email
                                </TabsTrigger>
                                <TabsTrigger value="api_key" className="data-[state=active]:bg-emerald-600">
                                    <Key className="h-4 w-4 mr-2" />
                                    API Key
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="email" className="mt-6">
                                <form onSubmit={handleEmailLogin} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="email" className="text-slate-300">Email</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                                            <Input
                                                id="email"
                                                type="email"
                                                placeholder="vendor@company.com"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="password" className="text-slate-300">Password</Label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                                            <Input
                                                id="password"
                                                type="password"
                                                placeholder="••••••••"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <Button
                                        type="submit"
                                        className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <ShieldCheck className="h-4 w-4 mr-2" />
                                        )}
                                        Sign In
                                    </Button>
                                </form>
                            </TabsContent>

                            <TabsContent value="api_key" className="mt-6">
                                <form onSubmit={handleApiKeyLogin} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="api_key" className="text-slate-300">API Key</Label>
                                        <div className="relative">
                                            <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                                            <Input
                                                id="api_key"
                                                type="password"
                                                placeholder="lbv_xxxxxxxxxxxx"
                                                value={apiKey}
                                                onChange={(e) => setApiKey(e.target.value)}
                                                className="pl-10 bg-slate-800/50 border-slate-700 text-white font-mono placeholder:text-slate-500"
                                                required
                                            />
                                        </div>
                                        <p className="text-xs text-slate-500">
                                            Your API key was provided when you registered
                                        </p>
                                    </div>

                                    <Button
                                        type="submit"
                                        className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <ArrowRight className="h-4 w-4 mr-2" />
                                        )}
                                        Access Dashboard
                                    </Button>
                                </form>
                            </TabsContent>
                        </Tabs>

                        <div className="mt-6 pt-6 border-t border-slate-800 text-center">
                            <p className="text-sm text-slate-400">
                                Don't have an account?{' '}
                                <Link href="/vendor/register" className="text-emerald-400 hover:text-emerald-300 font-medium">
                                    Register as a Vendor
                                </Link>
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
